import { useState } from "react";

import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { IFoldersFilters } from "../../../types";

type IRadioButtonValue = "isAll" | "isNew" | "isTrending";

const getRadioItems = () => {
    const params = proNavigator.getApiParams();

    const disabledTooltip = translate("filters.title.disabled.tooltip");
    const disabled = Boolean(params.isWindow);
    return [
        {
            id: "isAll",
            text: translate("filters.title.all"),
            tooltip: translate("folders.filters.title.all.tooltip"),
            trackingName: "All",
            disabled: false,
        },
        {
            id: "isNew",
            text: translate("filters.title.new"),
            tooltip: disabled ? disabledTooltip : translate("folders.filters.title.new.tooltip"),
            trackingName: "New",
            disabled,
        },
        {
            id: "isTrending",
            text: translate("filters.title.trending"),
            tooltip: disabled ? disabledTooltip : translate("folders.filters.title.trending.tooltip"),
            trackingName: "Trending",
            disabled,
        },
    ];
};

export const useRadioFilters = (
    initialFilters: IFoldersFilters,
    onFilterChange?: (filters: IFoldersFilters) => void,
    externalRadioFilters?: { isAll: boolean; isNew: boolean; isTrending: boolean },
) => {
    const getSelectedRadioButton = (): IRadioButtonValue => {
        if (externalRadioFilters) {
            if (externalRadioFilters.isNew) {
                return "isNew";
            }
            if (externalRadioFilters.isTrending) {
                return "isTrending";
            }
            return "isAll";
        }
        if (initialFilters.isNew) {
            return "isNew";
        }
        if (initialFilters.isTrending) {
            return "isTrending";
        }
        return "isAll";
    };

    const [selectedRadioButton, setSelectedRadioButton] = useState<IRadioButtonValue>(getSelectedRadioButton());
    const [filters, setFilters] = useState<IFoldersFilters>(initialFilters);

    const radioButtonItems = getRadioItems();

    const onRadioButtonClicked = (item) => {
        const selectedValueObject = radioButtonItems.find((radioItem) => radioItem.id === item);
        if (!selectedValueObject || selectedValueObject.disabled) {
            return;
        }

        setSelectedRadioButton(selectedValueObject.id as IRadioButtonValue);
        allTrackers.trackEvent("Filter", "click", selectedValueObject.trackingName);

        const newFilters = {
            ...filters,
            isAll: undefined,
            isNew: undefined,
            isTrending: undefined,
            [selectedValueObject.id]: true,
        };

        setFilters(newFilters);
        onFilterChange(newFilters);
    };

    return {
        selectedRadioButton,
        filters,
        radioButtonItems,
        onRadioButtonClicked,
        setFilters,
    };
};
